import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Modal } from "../../components/Modal/Modal";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";

import { AdminUser, demoteUser } from "./api";
import { UserPreviewCard } from "./UserPreviewCard";
import { RoleArrow } from "./RoleArrow";
import styles from "./UsersPage.module.css";

interface Props {
  open: boolean;
  user: AdminUser | null;
  /** True when current user is the only active super_admin. */
  isLastSuperAdmin: boolean;
  /** True when the targeted row is the current user. */
  isSelf: boolean;
  onClose: () => void;
}

/**
 * Demote-to-Member confirmation modal. Mockup L557-L585.
 *
 * Branches into two variants:
 * - "blocked" — when the current user is the last super_admin and they're
 *   trying to demote themselves: surfaces a warn Alert explaining the rule,
 *   single Close button, no destructive primary.
 * - "normal" — otherwise: user-preview card, Super admin → Member arrow,
 *   warn Alert about privilege removal, danger CTA.
 */
export function DemoteModal({
  open,
  user,
  isLastSuperAdmin,
  isSelf,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const { push } = useToast();

  const mut = useMutation({
    mutationFn: (id: string) => demoteUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
      push({
        variant: "success",
        message: "User demoted to member.",
      });
      onClose();
    },
    onError: (err: unknown) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  if (!user) return null;

  const blocked = isLastSuperAdmin && isSelf;

  if (blocked) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Can't demote yourself"
        footer={
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        }
      >
        <UserPreviewCard user={user} isSelf />
        <Alert variant="warn" title="You're the last super admin">
          Currently 1 active super admin (you). Demoting yourself would leave
          the platform with no super admin. Promote another user first, then
          return here to demote yourself.
        </Alert>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Demote ${user.name} to Member?`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mut.mutate(user.id)}
            disabled={mut.isPending}
            loading={mut.isPending}
          >
            Demote to Member
          </Button>
        </>
      }
    >
      <UserPreviewCard user={user} isSelf={isSelf} />
      <RoleArrow from="super_admin" to="project_user" />
      <Alert variant="warn">
        Demoting only removes their platform-wide privileges. They keep their
        project memberships.
      </Alert>
      <p className={styles.modalFootnote}>
        If promoted again later, this user returns straight to super admin —
        no re-onboarding needed.
      </p>
    </Modal>
  );
}
